import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Fade,
  Slide,
  Stack
} from '@mui/material';
import {
  AutoFixHigh,
  Spellcheck,
  MenuBook,
  Style,
  ContentCopy,
  Save as SaveIcon,
  Refresh,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import SuggestionPanel from './SuggestionPanel';
import TextAnalytics from './TextAnalytics';
import WritingAssistant from './WritingAssistant';
import RichTextEditor from './TextEditorCore';
import ActionBar from '../common/ActionBar';
import InlineFolderSelector from '../common/InlineFolderSelector';
import claudeService from '../../services/claudeService';
import api from '../../services/api';
import { useAppTheme } from '../../utils/themeUtils';
import { useFolderContext } from '../../context/FolderContext';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const DocsMain = () => {
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState({ start: 0, end: 0 });
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);
  
  // Use centralized theme
  const { colors, isDark, muiTheme } = useAppTheme();
  const { selectedFolderId } = useFolderContext();
  const { user } = useAuth() || { user: null };

  // Analysis selection state - default to 'all' selected
  const [selectedAnalysisTypes, setSelectedAnalysisTypes] = useState(['all']);
  const [showWritingAssistant, setShowWritingAssistant] = useState(false); // Default hidden

  // Toggle analysis type selection
  const toggleAnalysisType = (type) => {
    setSelectedAnalysisTypes(prev => {
      if (type === 'all') {
        // If 'all' is clicked, toggle between 'all' and empty
        return prev.includes('all') ? [] : ['all'];
      } else {
        // If individual type is clicked, remove 'all' and toggle the specific type
        const withoutAll = prev.filter(t => t !== 'all');
        if (withoutAll.includes(type)) {
          return withoutAll.filter(t => t !== type);
        } else {
          return [...withoutAll, type];
        }
      }
    });
  };

  // Claude API integration
  const analyzeWithClaude = useCallback(async (analysisType = 'comprehensive') => {
    // Get text content from the editor
    const textToAnalyze = editorRef.current?.getPlainText() || '';
    
    if (!textToAnalyze.trim()) {
      showNotification('Please enter some text to analyze', 'warning');
      return;
    }

    setIsAnalyzing(true);
    
    try {
      // Use Claude service for analysis
      const parsedSuggestions = await claudeService.analyzeText(textToAnalyze, analysisType);
      
      setSuggestions(parsedSuggestions);
      setShowSuggestions(true);
      
      // Generate analytics using the service
      const analyticsData = {
        ...claudeService.generateAnalytics(textToAnalyze, parsedSuggestions),
        analysisType
      };
      
      setAnalytics(analyticsData);
      showNotification(`Analysis complete! Found ${parsedSuggestions.length} suggestions.`, 'success');
      
    } catch (error) {
      console.error('Claude API error:', error);
      showNotification('Failed to analyze text. Please try again.', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  // Perform analysis based on selected types
  const performSelectedAnalysis = async () => {
    if (selectedAnalysisTypes.length === 0) {
      showNotification('Please select at least one analysis type', 'warning');
      return;
    }

    // Determine analysis type for backend call
    let analysisType;
    if (selectedAnalysisTypes.includes('all')) {
      analysisType = 'comprehensive'; // Backend call for all analysis types
    } else if (selectedAnalysisTypes.length === 1) {
      analysisType = selectedAnalysisTypes[0]; // Single analysis type
    } else {
      analysisType = 'comprehensive'; // Multiple individual types = comprehensive
    }
    
    await analyzeWithClaude(analysisType);
  };

  // Handle text selection from rich text editor
  const handleTextSelect = useCallback((selection) => {
    setSelectedText(selection.text);
    setSelectionRange({ start: selection.start, end: selection.end });
  }, []);

  // Handle content change from rich text editor
  const handleContentChange = useCallback((htmlContent) => {
    setHtmlContent(htmlContent);
    // Extract plain text for AI analysis
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    setText(plainText);
  }, []);

  // Claude API integration
  // Apply suggestion
  const applySuggestion = useCallback((suggestion) => {
    if (editorRef.current) {
      // Get current HTML content
      const currentHtml = editorRef.current.getHtmlContent();
      
      // For rich text, we need to be more careful about replacements
      // Convert HTML to text for finding the original text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = currentHtml;
      const plainText = tempDiv.textContent || tempDiv.innerText || '';
      
      // Replace in plain text first
      const newPlainText = plainText.replace(suggestion.original, suggestion.suggestion);
      
      // For now, we'll replace the entire content with the new text
      // In a more sophisticated implementation, you'd preserve formatting
      editorRef.current.setHtmlContent(newPlainText);
      
      // Update states
      setText(newPlainText);
      setHtmlContent(newPlainText);
    }
    
    // Remove applied suggestion
    setSuggestions(prev => prev.filter(s => s !== suggestion));
    showNotification('Suggestion applied successfully!', 'success');
  }, []);

  // Show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  // Copy text to clipboard
  const copyToClipboard = async () => {
    try {
      // Copy both HTML and plain text
      const htmlContent = editorRef.current?.getHtmlContent() || '';
      const plainText = editorRef.current?.getPlainText() || '';
      
      if (navigator.clipboard && window.ClipboardItem) {
        // Modern clipboard API with HTML support
        const clipboardItem = new ClipboardItem({
          'text/html': new Blob([htmlContent], { type: 'text/html' }),
          'text/plain': new Blob([plainText], { type: 'text/plain' })
        });
        await navigator.clipboard.write([clipboardItem]);
      } else {
        // Fallback to plain text
        await navigator.clipboard.writeText(plainText);
      }
      
      showNotification('Text copied to clipboard!', 'success');
    } catch (error) {
      showNotification('Failed to copy text', 'error');
    }
  };

  // Save text - now with proper document API
  const saveText = async () => {
    setSaving(true);
    try {
      const htmlContent = editorRef.current?.getHtmlContent() || '';
      const plainText = editorRef.current?.getPlainText() || '';
      
      // Create document data matching backend structure
      const docData = {
        title: plainText.substring(0, 50).trim() || 'Untitled Document',
        content: htmlContent || plainText, // Backend will store this in S3
        type: 'RICH_TEXT', // Document.DocumentType.RICH_TEXT
        folderId: selectedFolderId || null,
        tags: ['pai-doc', 'document'] // Tag as PAI document
      };
      
      console.log('Saving document:', docData);
      
      // Save using document API
      const response = await api.documents.create(docData);
      
      if (response && response.documentId) {
        showNotification(`Document saved successfully!`, 'success');
        // Optionally navigate to view the saved document after a short delay
        setTimeout(() => {
          if (window.confirm('Document saved! Would you like to view it?')) {
            navigate(`/document/${response.documentId}`);
          }
        }, 1000);
      } else {
        showNotification('Document saved successfully!', 'success');
      }
    } catch (error) {
      console.error('Error saving document:', error);
      
      // Provide more specific error messages
      if (error.response?.status === 413) {
        showNotification('Document is too large to save', 'error');
      } else if (error.response?.status === 429) {
        showNotification('Rate limit exceeded. Please try again later.', 'error');
      } else if (error.response?.status === 401) {
        showNotification('Please log in to save documents', 'error');
      } else if (error.response?.data?.message) {
        showNotification(`Failed to save: ${error.response.data.message}`, 'error');
      } else {
        showNotification('Failed to save document. Please try again.', 'error');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minHeight: '100vh',
        backgroundColor: isDark ? '#1a1a1a' : '#f1f3f4', // Slightly darker gray for better editor contrast
        color: colors.text,
        transition: 'all 0.3s ease',
        pt: 0,
        mt: 0,
        position: 'relative',
        top: 0,
        '& > *:first-child': {
          mt: '0 !important',
          pt: '0 !important',
        }
      }}
    >
      {/* Minimal, Elegant Header - Notion Style */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        style={{ 
          margin: 0, 
          padding: 0,
          position: 'relative',
          top: 0,
          display: 'block'
        }}
      >
        {/* Ultra-minimal header container */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            py: 0.5,
            px: 1.5,
            mb: 1,
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.01)', // Lighter in dark mode
            borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
            backdropFilter: 'blur(8px)',
            gap: { xs: 1, md: 0 }
          }}
        >
          {/* Left side: Title + Analysis Controls */}
          <Box 
            display="flex" 
            alignItems="center" 
            gap={{ xs: 2, md: 3 }}
            flexWrap={{ xs: 'wrap', md: 'nowrap' }}
            sx={{ 
              width: { xs: '100%', md: 'auto' },
              justifyContent: { xs: 'space-between', md: 'flex-start' }
            }}
          >
            {/* Refined Title */}
            <Typography 
              variant="h6" 
              component="h1" 
              sx={{
                fontWeight: 600,
                color: colors.text,
                fontSize: { xs: '1rem', md: '1.1rem' }, // Smaller on mobile
                letterSpacing: '-0.01em',
                mr: { xs: 0, md: 1 },
                minWidth: 'fit-content' // Prevent title from shrinking
              }}
            >
              P<i style={{ fontStyle: 'italic', opacity: 0.8 }}>AI</i> Doc
            </Typography>

            {/* Elegant dot separator - Hide on mobile */}
            <Box 
              sx={{ 
                width: 3, 
                height: 3, 
                borderRadius: '50%', 
                backgroundColor: colors.textSecondary,
                opacity: 0.4,
                display: { xs: 'none', md: 'block' }
              }} 
            />

            {/* Polished Analysis Chips - Notion Style */}
            <Box display="flex" alignItems="center" gap={1}>
              {[
                { key: 'grammar', label: 'Grammar' },
                { key: 'style', label: 'Style' },
                { key: 'tone', label: 'Tone' }
              ].map(({ key, label }) => (
                <Chip
                  key={key}
                  label={label}
                  onClick={() => {
                    setSelectedAnalysisTypes(prev => 
                      prev.includes(key) 
                        ? prev.filter(item => item !== key)
                        : [...prev, key]
                    );
                  }}
                  sx={{
                    height: 26, // Even more compact height
                    fontSize: '12px',
                    fontWeight: 500,
                    borderRadius: '13px', // Perfect pill shape
                    backgroundColor: selectedAnalysisTypes.includes(key) 
                      ? `${colors.primary}15` 
                      : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    color: selectedAnalysisTypes.includes(key) 
                      ? colors.primary 
                      : colors.text,
                    border: `1px solid ${selectedAnalysisTypes.includes(key) 
                      ? `${colors.primary}30` 
                      : 'transparent'}`,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      backgroundColor: selectedAnalysisTypes.includes(key)
                        ? `${colors.primary}20`
                        : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                    },
                    '& .MuiChip-label': {
                      px: 1.2, // Reduced padding
                      py: 0
                    }
                  }}
                />
              ))}
            </Box>

            {/* Elegant arrow separator */}
            <Box 
              sx={{ 
                color: colors.textSecondary,
                opacity: 0.5,
                fontSize: '14px',
                mx: 0.5
              }}
            >
              →
            </Box>

            {/* Polished Analyze Button */}
            <Button
              onClick={performSelectedAnalysis}
              disabled={isAnalyzing || selectedAnalysisTypes.length === 0}
              sx={{
                height: 28, // Match chip height
                px: 2,
                borderRadius: '14px', // Match chip radius
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: colors.primary,
                color: '#ffffff',
                border: 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: colors.primaryDark || colors.primary,
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                '&:disabled': {
                  backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                  color: colors.textSecondary,
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze'}
            </Button>
          </Box>

          {/* Right side: Actions */}
          <Box display="flex" alignItems="center" gap={1.5}>
            {/* Folder Selector - Properly Working */}
            <InlineFolderSelector 
              variant="compact" 
              size="small"
              placeholder="📁 Folder"
              sx={{ 
                minWidth: 80,
                '& .MuiButton-root': {
                  height: 28,
                  minWidth: 'auto',
                  px: 1.5,
                  borderRadius: '14px',
                  fontSize: '12px',
                  fontWeight: 500,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  color: colors.text,
                  border: 'none',
                  textTransform: 'none',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                    transform: 'translateY(-1px)'
                  }
                }
              }}
            />

            {/* Enhanced Save Button */}
            <Button
              onClick={saveText}
              disabled={saving}
              sx={{
                height: 28,
                minWidth: 'auto',
                px: 2,
                borderRadius: '14px',
                fontSize: '12px',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: saving 
                  ? (isDark ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.1)')
                  : (isDark ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.1)'),
                color: saving 
                  ? (isDark ? 'rgba(34, 197, 94, 0.7)' : 'rgba(34, 197, 94, 0.8)')
                  : (isDark ? '#22c55e' : '#16a34a'),
                border: `1px solid ${saving 
                  ? (isDark ? 'rgba(34, 197, 94, 0.3)' : 'rgba(34, 197, 94, 0.2)')
                  : (isDark ? 'rgba(34, 197, 94, 0.4)' : 'rgba(34, 197, 94, 0.3)')}`,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                  borderColor: isDark ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.4)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.2)'
                },
                '&:active': {
                  transform: 'translateY(0px)',
                },
                '&:disabled': {
                  backgroundColor: isDark ? 'rgba(34, 197, 94, 0.05)' : 'rgba(34, 197, 94, 0.05)',
                  color: isDark ? 'rgba(34, 197, 94, 0.5)' : 'rgba(34, 197, 94, 0.6)',
                  borderColor: isDark ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.15)',
                  transform: 'none',
                  boxShadow: 'none'
                }
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>

            {/* Polished Assistant Toggle */}
            <IconButton 
              onClick={() => setShowWritingAssistant(!showWritingAssistant)}
              sx={{ 
                width: 28, // Compact size
                height: 28,
                borderRadius: '14px',
                backgroundColor: showWritingAssistant 
                  ? `${colors.primary}15` 
                  : isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                color: showWritingAssistant ? colors.primary : colors.text,
                border: showWritingAssistant ? `1px solid ${colors.primary}30` : 'none',
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: showWritingAssistant
                    ? `${colors.primary}20`
                    : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                },
                '&:active': {
                  transform: 'translateY(0px)',
                }
              }}
            >
              <AutoFixHigh sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </Box>
      </motion.div>

      {/* Main Content - Responsive Layout */}
      <Box display="flex" gap={3} sx={{ backgroundColor: 'transparent' }}>
        {/* PA| Editor - Dynamic Width */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          style={{ 
            flex: showSuggestions 
              ? '1 1 60%' 
              : showWritingAssistant 
                ? '1 1 60%' 
                : '1 1 100%', // Full width when no assistant
            backgroundColor: 'transparent' // Ensure no background interference
          }}
        >
          {/* Removed unnecessary Paper wrapper - cleaner structure */}
          <RichTextEditor
            ref={editorRef}
            value={htmlContent}
            onChange={handleContentChange}
            onSelect={handleTextSelect}
            placeholder="Start writing your document..."
            theme={colors}
            isDark={isDark}
            // Removed fixed height - now grows naturally with content
          />
        </motion.div>

        {/* Right Side - Conditional Assistant/Analysis */}
        <AnimatePresence>
          {(showSuggestions || showWritingAssistant) && (
            <motion.div
              initial={{ opacity: 0, x: 20, width: 0 }}
              animate={{ opacity: 1, x: 0, width: 'auto' }}
              exit={{ opacity: 0, x: 20, width: 0 }}
              transition={{ duration: 0.3 }}
              style={{ flex: '1 1 40%' }}
            >
              <AnimatePresence mode="wait">
                {showSuggestions ? (
                  // Show Analysis Results
                  <motion.div
                    key="suggestions"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SuggestionPanel
                      suggestions={suggestions}
                      onApplySuggestion={applySuggestion}
                      onClose={() => setShowSuggestions(false)}
                      theme={colors}
                    />
                  </motion.div>
                ) : showWritingAssistant ? (
                  // Show Writing Assistant
                  <motion.div
                    key="assistant"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <WritingAssistant
                      selectedText={selectedText}
                      onSuggestion={(suggestion) => {
                        // Optional: Handle suggestion preview
                      }}
                      onApplySuggestion={(suggestion) => {
                        if (selectedText && editorRef.current) {
                          try {
                            const selection = window.getSelection();
                            if (selection.rangeCount > 0) {
                              const range = selection.getRangeAt(0);
                              range.deleteContents();
                              range.insertNode(document.createTextNode(suggestion));
                              selection.removeAllRanges();
                              
                              const contentEditableElement = document.querySelector('[contenteditable="true"]');
                              if (contentEditableElement) {
                                const inputEvent = new Event('input', { bubbles: true });
                                contentEditableElement.dispatchEvent(inputEvent);
                                const newContent = contentEditableElement.innerHTML;
                                setHtmlContent(newContent);
                                handleContentChange(newContent);
                              }
                              
                              setSelectedText('');
                              showNotification('Writing suggestion applied successfully!', 'success');
                            } else {
                              showNotification('Please select text first', 'warning');
                            }
                          } catch (error) {
                            console.error('Error applying suggestion:', error);
                            showNotification('Failed to apply suggestion', 'error');
                          }
                        } else {
                          showNotification('Please select text first', 'warning');
                        }
                      }}
                      theme={colors}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      {/* Analytics Panel */}
      {analytics && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Box mt={2}>
            <TextAnalytics analytics={analytics} theme={colors} />
          </Box>
        </motion.div>
      )}

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocsMain;
